import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { RefreshCw } from 'lucide-react';
import { iconBtnCls } from './styles';

interface PageHeaderProps {
  /** Page title (string or node). */
  title: ReactNode;
  /** Optional leading icon, rendered in the platform accent. */
  icon?: LucideIcon;
  /** Optional record count rendered after the title as "(1,234)". */
  count?: number | null;
  /** One quiet line under the title; omit for none. */
  subtitle?: ReactNode;
  /** Right-aligned actions (buttons, menus, toggles). */
  actions?: ReactNode;
  className?: string;
  /**
   * Phone (below `sm`) policy for the title block, nothing from `sm` up:
   * - `'auto'` (default): the title row's own width decides whether the actions
   *   share the row (the subtitle never widens it, see below); an action group
   *   that does not fit wraps under the title, right-aligned, and the title
   *   keeps its full width.
   * - `'truncate'`: the title always yields to the actions and truncates — for
   *   an identity bar whose actions must stay on the name's row (the lead
   *   page's Call and ⋯).
   */
  phoneTitle?: 'auto' | 'truncate';
}

/**
 * Shared page header: one compact title row for every page in every tool —
 * icon + title + optional count/subtitle on the left, actions on the right.
 * Replaces the hand-rolled headers that had drifted across modules (h1 vs h2,
 * text-xl vs text-lg vs font-bold, multiple refresh-button variants).
 *
 * Phones (below `sm`) only: the title block grows into the room the actions
 * leave, and the subtitle is laid out at the block's final width without
 * widening it (`w-0 min-w-full`), so a long subtitle no longer pushes a lone
 * refresh button onto a line of its own; an action group that does not fit
 * beside the title wraps under it RIGHT-aligned — where every other action
 * group sits, not stranded at the left. From `sm` up the layout is exactly
 * what it was.
 */
export default function PageHeader({ title, icon: Icon, count, subtitle, actions, className = '', phoneTitle = 'auto' }: PageHeaderProps) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${className}`}>
      <div className={`min-w-0 ${phoneTitle === 'truncate' ? 'max-sm:flex-1' : 'max-sm:grow'}`}>
        <h1 className="flex items-center gap-2 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
          {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />}
          <span className="min-w-0 truncate">{title}</span>
          {typeof count === 'number' && (
            <span className="flex-shrink-0 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({count.toLocaleString()})
            </span>
          )}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 max-sm:w-0 max-sm:min-w-full">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 max-sm:ml-auto">{actions}</div>}
    </div>
  );
}

/** The one standard refresh affordance for page headers. */
export function RefreshButton({
  onClick,
  loading = false,
  label = 'Refresh',
}: {
  onClick: () => void;
  loading?: boolean;
  label?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={loading} title={label} aria-label={label} className={iconBtnCls}>
      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
    </button>
  );
}
